/**
 * Singapore Timezone Helper Functions
 * 
 * These utilities ensure all date operations use Singapore timezone (Asia/Singapore)
 * regardless of the user's local timezone, preventing off-by-one date errors.
 */

import { toZonedTime, format as formatTz } from 'date-fns-tz';
import { startOfDay, addDays } from 'date-fns';

export const SINGAPORE_TZ = 'Asia/Singapore';

/**
 * Gets current date/time in Singapore timezone
 * @param {Date} date - Optional date to convert, defaults to now
 * @returns {Date} Date object representing Singapore time
 */
export const getSingaporeDate = (date = new Date()) => {
    return toZonedTime(date, SINGAPORE_TZ);
};

/**
 * Formats a date to YYYY-MM-DD in Singapore timezone
 * This solves the problem where date.toISOString().split("T")[0] 
 * returns the wrong date for users in different timezones.
 * 
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string (YYYY-MM-DD)
 * 
 * @example
 * // User in Pacific time selects Oct 30, 2025 at 11:38 PM
 * const date = new Date('Thu Oct 30 2025 23:38:20 GMT-0700');
 * date.toISOString().split("T")[0]  // Returns "2025-10-31" ❌ (UTC time)
 * formatDateSingapore(date)         // Returns "2025-10-30" ✅ (Singapore time)
 */
export const formatDateSingapore = (date) => {
    const sgDate = toZonedTime(date, SINGAPORE_TZ);
    return formatTz(sgDate, 'yyyy-MM-dd', { timeZone: SINGAPORE_TZ });
};

/**
 * Creates a Date object for a specific day in Singapore timezone
 * Ensures the date represents midnight in Singapore time
 * @param {Date} date - Date to convert
 * @returns {Date} Date object at midnight Singapore time
 */
export const createSingaporeDate = (date) => {
    const sgDate = toZonedTime(date, SINGAPORE_TZ);
    return startOfDay(sgDate);
};

/**
 * Adds days to a Singapore date
 * @param {Date} date - Starting date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {Date} New date with days added
 */
export const addDaysSingapore = (date, days) => {
    const sgDate = toZonedTime(date, SINGAPORE_TZ);
    return addDays(sgDate, days);
};

/**
 * Gets the next day in Singapore timezone
 * @param {Date|string} dateString - Date to increment
 * @returns {string} Next day formatted as YYYY-MM-DD
 */
export const getNextDateSingapore = (dateString) => {
    const currentDate = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const nextDay = addDaysSingapore(currentDate, 1);
    return formatDateSingapore(nextDay);
};

